import { Alert } from 'antd';
import { DASHBOARD_ROW_GRID, DASHBOARD_ROW_STACK, TILE_SPAN } from '@/lib/dashboardGrid';
import { groupTilesByRow } from '@pams/shared';
import { resolveTileWords } from '@/lib/tileWords';
import { TileCard } from '@/pages/Dashboard/TileCard';
import { tileDrillReach, tileSkeleton } from '@/pages/Dashboard/tiles/registry';
import { LAYOUT_COPY } from '../copy';
import type { LayoutDraft } from '../draft';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  §6 — A STRUCTURAL PREVIEW. THE LAYOUT, NEVER THE NUMBERS. [P6b]
 *
 *  A super admin editing the DIVISION layout is not a divisional nodal and cannot see what
 *  they built. This draws the real card, at its real width, with the tile's OWN silhouette —
 *  `tileSkeleton(id)` is the same function the dashboard uses while a tile loads — so the
 *  arrangement is exactly what the operator will get and nothing on screen is a figure that
 *  could be read as a division's real one.
 *
 *  ⚠️ NO IMPERSONATION PATH EXISTS, AND IT IS A PROPERTY OF THE SHAPE RATHER THAN A PROMISE.
 *  This component takes a DRAFT and a target LABEL. There is no `orgUnitId`, no officer, no
 *  token and no `Scope` — no parameter through which a subject could be supplied, which is
 *  `lib/policyPreview.ts`'s guarantee. It issues no request of any kind: everything it renders
 *  comes from local state and the client's own catalogue. `impersonation.test.ts` reads the
 *  source of this directory and fails on the vocabulary; `dashboardLayouts.spec.ts` fails the
 *  run if a `/dashboard` request is made from this route at all.
 *
 *  ⚠️ AND THE GRID IS UNNAMED ON PURPOSE. `DASHBOARD_GRID` and `TILE_SPAN` are `@min-`
 *  container queries with no container name, so they resolve to `AppShell`'s `<main>` — the
 *  same ancestor they resolve to on `/dashboard`. That is what makes the preview's column
 *  count IDENTICAL to the real thing at the same viewport, for free. It is also why this
 *  section is full width BELOW the two-column block: inside a 400px column `<main>` still
 *  answers 1062 at the floor, so `grid-cols-3` would apply inside 400px and draw three 122px
 *  columns — broken, and silently, because jsdom would report exactly the right classes.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const LayoutPreview = ({ draft, label }: { draft: LayoutDraft; label: string }) => (
  <section aria-labelledby="layout-preview-heading" className="flex flex-col gap-3">
    <h2 id="layout-preview-heading" className="text-sm font-medium text-foreground">
      {LAYOUT_COPY.previewHeading} · {label}
    </h2>
    <Alert type="info" showIcon message={LAYOUT_COPY.previewBanner} data-testid="preview-banner" />

    {/* ⚠️ [P6b.2] THE SAME ROW GROUPING THE DASHBOARD USES — one shared derivation, so a
        preview cannot show an arrangement the real screen would not. */}
    <div className={DASHBOARD_ROW_STACK} data-testid="layout-preview-grid">
      {groupTilesByRow(draft.entries).map((group) => (
        <div key={group[0]?.instanceId ?? 'row'} className={DASHBOARD_ROW_GRID} data-testid="layout-preview-row">
          {group.map((entry) => {
            const words = resolveTileWords(entry.tileId, entry);
            return (
              <TileCard
                key={entry.instanceId}
                id={entry.tileId}
                instanceId={entry.instanceId}
                title={words.title}
                hint={words.hint}
                spanClassName={TILE_SPAN[entry.size]}
                status="preview"
                skeleton={tileSkeleton(entry.tileId)}
                drillReach={tileDrillReach(entry.tileId)}
                /* ⚠️ `to: ''` NEVER BECOMES AN ANCHOR — `TileCard` refuses a link under `preview`.
               The LABEL still renders, because the operator may have overridden it. */
                drill={{ to: '', label: words.drill }}
              >
                {null}
              </TileCard>
            );
          })}
        </div>
      ))}
    </div>
    <p className="text-xs text-muted-foreground">{LAYOUT_COPY.previewDrillNote}</p>
  </section>
);
